// src/utils/calendar.js
import { Capacitor } from "@capacitor/core";
import { CapacitorCalendar } from "@ebarooni/capacitor-calendar";

function isNative() {
    return Capacitor.isNativePlatform();
}

function safeString(v) {
    return String(v || "").trim();
}

function coerceDateOnly(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function parseTimePart(value) {
    const raw = String(value || "").trim();
    const m = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;

    const hh = Number(m[1]);
    const mm = Number(m[2]);

    if (
        !Number.isInteger(hh) ||
        !Number.isInteger(mm) ||
        hh < 0 ||
        hh > 23 ||
        mm < 0 ||
        mm > 59
    ) {
        return null;
    }

    return { hh, mm };
}

function parseTimeRange(slot) {
    const raw = String(slot || "").trim();
    if (!raw) return null;

    const normalized = raw.replace(/\s+/g, "");
    const parts = normalized.split("-");
    if (parts.length !== 2) return null;

    const start = parseTimePart(parts[0]);
    const end = parseTimePart(parts[1]);

    if (!start || !end) return null;
    return { start, end };
}

function buildDateTime(dateValue, timeObj) {
    const base = coerceDateOnly(dateValue);
    if (!base || !timeObj) return null;

    const d = new Date(base);
    d.setHours(timeObj.hh, timeObj.mm, 0, 0);
    return d;
}

function fallbackWindow(dateValue) {
    const base = coerceDateOnly(dateValue);
    if (!base) return null;

    const start = new Date(base);
    start.setHours(9, 0, 0, 0);

    const end = new Date(base);
    end.setHours(10, 0, 0, 0);

    return { start, end };
}

function eventWindowFromDateAndSlot(dateValue, slot) {
    const range = parseTimeRange(slot);
    if (range) {
        const start = buildDateTime(dateValue, range.start);
        const end = buildDateTime(dateValue, range.end);
        if (start && end && end > start) {
            return { start, end };
        }
    }
    return fallbackWindow(dateValue);
}

function buildSupplierPickupLocation(order) {
    const addr = order?.pickup_address;
    if (addr?.full_address) return safeString(addr.full_address);
    return "";
}

function buildCustomerLocation(order) {
    if (order?.pickup_address?.full_address) {
        return safeString(order.pickup_address.full_address);
    }
    return "";
}

function buildLocation({ order, actor = "customer" }) {
    if (actor === "supplier") {
        return buildSupplierPickupLocation(order);
    }
    return buildCustomerLocation(order);
}

function buildTitle({ kind, order }) {
    const orderId = safeString(order?.id);
    return kind === "pickup"
        ? `Ritiro ordine Stirup #${orderId}`
        : `Consegna ordine Stirup #${orderId}`;
}

async function ensureCalendarPermissions() {
    const check = await CapacitorCalendar.checkPermission({
        scope: "writeCalendar",
    });

    if (check?.result === "granted") return true;

    const req = await CapacitorCalendar.requestPermission({
        scope: "writeCalendar",
    });

    return req?.result === "granted";
}

async function createCalendarEvent({
    title,
    location,
    description,
    startDate,
    endDate,
}) {
    const granted = await ensureCalendarPermissions();
    if (!granted) {
        throw new Error("Permesso calendario non concesso");
    }

    await CapacitorCalendar.createEvent({
        title: safeString(title),
        location: safeString(location),
        description: safeString(description),
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
        isAllDay: false,
    });
}

async function addOrderEvent({ kind, order, actor = "customer" }) {
    if (!isNative()) {
        throw new Error("Calendario disponibile solo su app mobile");
    }

    const dateValue =
        kind === "pickup" ? order?.data_ritiro : order?.data_consegna;
    const slot =
        kind === "pickup" ? order?.ritiro_fascia : order?.consegna_fascia;

    if (!dateValue) {
        throw new Error(
            kind === "pickup"
                ? "Data ritiro non disponibile"
                : "Data consegna non disponibile"
        );
    }

    const window = eventWindowFromDateAndSlot(dateValue, slot);
    if (!window?.start || !window?.end) {
        throw new Error("Impossibile costruire l'evento calendario");
    }

    await createCalendarEvent({
        title: buildTitle({ kind, order }),
        location: buildLocation({ order, actor }),
        startDate: window.start,
        endDate: window.end,
    });
}

export async function addOrderPickupToCalendar(order, options = {}) {
    return addOrderEvent({
        kind: "pickup",
        order,
        actor: options.actor || "customer",
    });
}

export async function addOrderDeliveryToCalendar(order, options = {}) {
    return addOrderEvent({
        kind: "delivery",
        order,
        actor: options.actor || "customer",
    });
}